// BL-24 — every withdrawal this staff member has asked for, on the shared table.
//
// ADMIN AND SUPERADMIN ONLY. `payout_requests` carries no grant for hub_manager
// or executive (0156, 0218), so the tab is not offered to them and the query is
// not enabled — the database would refuse it either way.
//
// ⚠ NOT the intake's 7-column daily ledger. "Daily balance", "total" and "total
// commission (cash only)" are defined nowhere, and inventing pay arithmetic is
// what never-invent-policy forbids — see the hook's header. Open for Luigi.

import {
  AdminDataTable,
  AdminTableIdentity,
  type AdminTableExportColumn,
  type AdminTableFacet,
} from "@bednbody/ui/components/admin-data-table";
import { Badge, type BadgeVariant } from "@bednbody/ui/components/badge";
import { formatPHP } from "@bednbody/ui/lib/format";
import type { Enums } from "@bednbody/types/database";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import type { RosterPerson } from "../components/types";
import { type StaffPayoutRow, useStaffPayouts } from "./staff-detail.hooks";

/** TEND-059's lifecycle. `pending` and `processing` still hold the amount. */
const STATUS_LABEL: Record<Enums<"payout_status">, string> = {
  pending: "Awaiting code",
  processing: "Processing",
  paid: "Paid",
  failed: "Failed",
  returned: "Returned",
  cancelled: "Cancelled",
};

const STATUS_TONE: Record<Enums<"payout_status">, BadgeVariant> = {
  pending: "warning-soft",
  processing: "info-soft",
  paid: "success-soft",
  failed: "destructive-soft",
  returned: "destructive-soft",
  cancelled: "muted-soft",
};

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" });

export function StaffPayoutHistory({ person }: { person: RosterPerson }) {
  const { data: payouts, isLoading, isError } = useStaffPayouts(person.id, true);
  const rows = useMemo(() => payouts ?? [], [payouts]);

  // Only the statuses this person actually has, so the chip row stays short.
  const facets: AdminTableFacet<StaffPayoutRow>[] = useMemo(() => {
    const present = new Set(rows.map((row) => row.status));
    return [
      {
        id: "status",
        label: "Status",
        options: (Object.keys(STATUS_LABEL) as Enums<"payout_status">[])
          .filter((status) => present.has(status))
          .map((status) => ({ value: status, label: STATUS_LABEL[status] })),
        getValue: (row) => row.status,
      },
    ];
  }, [rows]);

  const columns: ColumnDef<StaffPayoutRow, unknown>[] = [
    {
      id: "requested",
      accessorFn: (row) => row.createdAt,
      header: "Requested",
      cell: ({ row }) => (
        <AdminTableIdentity
          fallback="₱"
          subtitle={formatPHP(row.original.amount)}
          title={dateTime(row.original.createdAt)}
        />
      ),
    },
    {
      id: "status",
      accessorFn: (row) => row.status,
      header: "Status",
      cell: ({ row }) => (
        <Badge shape="soft" variant={STATUS_TONE[row.original.status]}>
          {STATUS_LABEL[row.original.status]}
        </Badge>
      ),
    },
    {
      id: "settled",
      accessorFn: (row) => row.settledAt ?? "",
      header: "Settled",
      cell: ({ row }) =>
        row.original.settledAt ? (
          dateTime(row.original.settledAt)
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: "reference",
      accessorFn: (row) => row.providerRef ?? "",
      header: "Reference",
      enableSorting: false,
      cell: ({ row }) =>
        row.original.providerRef ? (
          <span className="font-mono text-xs">{row.original.providerRef}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: "reason",
      accessorFn: (row) => row.failureReason ?? "",
      header: "Why it failed",
      enableSorting: false,
      cell: ({ row }) =>
        row.original.failureReason ? (
          <span className="text-destructive">{row.original.failureReason}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ];

  const exportColumns: AdminTableExportColumn<StaffPayoutRow>[] = [
    { header: "Requested", value: (row) => row.createdAt },
    { header: "Amount", value: (row) => row.amount },
    { header: "Status", value: (row) => STATUS_LABEL[row.status] },
    { header: "Settled", value: (row) => row.settledAt ?? "" },
    { header: "Reference", value: (row) => row.providerRef ?? "" },
    { header: "Why it failed", value: (row) => row.failureReason ?? "" },
  ];

  const paid = rows.filter((row) => row.status === "paid");
  const paidTotal = paid.reduce((sum, row) => sum + row.amount, 0);

  return (
    <AdminDataTable
      presentation="embedded"
      columns={columns}
      data={rows}
      description={
        payouts
          ? `${rows.length} ${rows.length === 1 ? "request" : "requests"} · ${formatPHP(paidTotal)} paid out`
          : "Loading…"
      }
      emptyDescription={
        isError
          ? "The payouts could not be loaded. Try again in a moment."
          : "They have not requested a withdrawal yet."
      }
      emptyTitle={isError ? "Couldn't load payouts" : "No payout requests"}
      exportColumns={exportColumns}
      exportFilename={`payouts-${person.name.toLowerCase().replace(/\s+/g, "-")}`}
      facets={facets}
      getRowId={(row) => row.id}
      initialSorting={[{ id: "requested", desc: true }]}
      loading={isLoading}
      searchPlaceholder="Search amount or reference…"
      searchText={(row) =>
        [String(row.amount), row.providerRef, STATUS_LABEL[row.status]].join(" ")
      }
      title="Payout history"
    />
  );
}
